import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { videos, categories } from '@/mocks/videos';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SeoHead from '@/components/feature/SeoHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import VideoCard from '@/components/base/VideoCard';
import { siteInfo } from '@/mocks/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideos = videos.filter((v) => article.relatedVideoIds.includes(v.id));
  const categoryName = categories.find((c) => c.slug === article.category)?.name || '';

  return (
    <>
      <ScrollToTop />
      <SeoHead
        title={`${article.title} - 国产在线观看影视资讯`}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',国产在线观看,国产影视资讯'}
        canonicalUrl={`${siteInfo.url}/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
            { '@type': 'ListItem', position: 2, name: '影视资讯', item: `${siteInfo.url}/category/${article.category}` },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        }}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.coverImage,
          datePublished: article.date,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: siteInfo.name,
          },
        }}
      />

      <Navbar />
      <main className="min-h-screen pt-16">
        <article>
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '影视资讯', href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />

              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                    {categoryName}
                  </span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line text-xs"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line text-xs"></i>
                    {article.date}
                  </span>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-xl aspect-[2/1] mb-10">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 国产在线观看`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none mb-14 text-foreground-800 leading-relaxed
                  [&>h4]:font-heading [&>h4]:text-lg [&>h4]:font-bold [&>h4]:text-foreground-950 [&>h4]:mt-8 [&>h4]:mb-3
                  [&>p]:mb-4 [&>p]:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {relatedVideos.length > 0 && (
                <div className="border-t border-background-200/70 pt-10 mb-14">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-6">
                    相关视频推荐
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                    {relatedVideos.map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}